import React, { useState, useEffect } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    FormControl,
    FormControlLabel,
    Checkbox,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress,
} from "@mui/material";
import { Contact, Customer } from "../types";
import { API_BASE_URL } from "../config";
import axiosExt from "../axios";
import { validateEmail } from "../utils/validation";
import { useTranslation } from "react-i18next";

interface AddContactDialogProps {
    open: boolean;
    onClose: () => void;
    onContactAdded: (contact: Contact) => void;
    onContactUpdated?: (contact: Contact) => void;
    selectedContact?: Contact | null;
    allowSurvey: boolean; 
    email: string;
    customerId?: string;
}

const AddContactDialog: React.FC<AddContactDialogProps> = ({
    open,
    onClose,
    onContactAdded,
    onContactUpdated,
    selectedContact, 
    allowSurvey,
    email = "",
    customerId = "",
}) => {
    const [contact, setContact] = useState<Contact>({
        id: "",
        firstName: "",
        lastName: "",
        email,
        mobile: "",
        phone: "",
        customerId,
        title: "",
        emailKey: "",
        sendSatisfactionSurvey: false,
    });

    const { t } = useTranslation();

    const [customers, setCustomers] = useState<Customer[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);
    const [helperText, setHelperText] = useState("");

    useEffect(() => {
        if (open) {
            fetchCustomers();

            // If we have a selectedContact (editing), use its data
            if (selectedContact && selectedContact.id) {
                setContact(selectedContact);
            } else {
                // Otherwise, initialize with empty values (adding new)
                setContact({
                    id: "",
                    firstName: "",
                    lastName: "",
                    email,
                    mobile: "",
                    phone: "",
                    customerId,
                    title: "",
                    emailKey: "",
                    sendSatisfactionSurvey:false,
                });
            }

            // Reset error state
            setError(false);
            setHelperText("");
        }
    }, [open, selectedContact, email, customerId]);

    const fetchCustomers = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await axiosExt.get<Customer[]>(`${API_BASE_URL}/customers`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setCustomers(response.data);
        } catch (error) {
            console.error("Error loading customers:", error);
        }
    };

    const handleChange = (field: keyof Contact, value: string) => {
        setContact((prev) => ({ ...prev, [field]: value }));
    };

    const handleSave = async () => {
        if (!validateEmail(contact.email)) {
            setError(true);
            setHelperText("Invalid email format");
            return;
        }

        setLoading(true);

        try {
            const token = localStorage.getItem("token");

            if (contact.id) {
                // Update existing contact
                await axiosExt.put(`${API_BASE_URL}/contacts/${contact.id}`, contact, {
                    headers: { Authorization: `Bearer ${token}` },
                });

                if (onContactUpdated) {
                    onContactUpdated(contact);
                }
            } else {
                // Add new contact
                const payload = { ...contact };
                delete payload.id;
                if (!payload.customerId) delete payload.customerId;

                const response = await axiosExt.post(`${API_BASE_URL}/contacts`, payload, {
                    headers: { Authorization: `Bearer ${token}` },
                });

                onContactAdded(response.data);
            }

            onClose();
        } catch (error) {
            console.error("Error saving contact:", error);
        } finally {
            setLoading(false);
        }
    };

    //const handleSave = async () => {
    //    if (!validateEmail(contact.email)) {
    //        setError(true);
    //        setHelperText("Invalid email format");
    //        return;
    //    }
    //    setLoading(true);
    //    try {
    //        const token = localStorage.getItem("token");
    //        const payload = { ...contact };
    //        delete payload.id;
    //        if (!payload.customerId) delete payload.customerId;
    //        const response = await axiosExt.post(`${API_BASE_URL}/contacts`, payload, {
    //            headers: { Authorization: `Bearer ${token}` },
    //        });
    //        if (contact.id) {
    //            if (onContactUpdated) {
    //                onContactUpdated(response.data);
    //            }
    //        }
    //        else {
    //            onContactAdded(response.data);
    //        }
            
    //        onClose();
    //    } catch (error) {
    //        console.error("Error adding contact:", error);
    //    } finally {
    //        setLoading(false);
    //    }
    //};

    const isEditing = contact.id !== "";

    return (
        <Dialog open={open} onClose={onClose}>
            <DialogTitle>{isEditing ? t("Edit Contact") : t("Add Contact")}</DialogTitle>
            <DialogContent>
                <TextField
                    autoFocus
                    margin="dense"
                    label={t("Email")}
                    type="email"
                    fullWidth
                    value={contact.email}
                    onChange={(e) => handleChange("email", e.target.value)}
                    error={error}
                    helperText={helperText}
                />
                <TextField
                    margin="dense"
                    label={t("First Name")}
                    fullWidth
                    value={contact.firstName}
                    onChange={(e) => handleChange("firstName", e.target.value)}
                />
                <TextField
                    margin="dense"
                    label={t("Last Name")}
                    fullWidth
                    value={contact.lastName}
                    onChange={(e) => handleChange("lastName", e.target.value)}
                />
                <FormControl fullWidth margin="dense">
                    <InputLabel>{t("Customer")}</InputLabel>
                    <Select
                        value={contact.customerId || ""}
                        onChange={(e) => handleChange("customerId", e.target.value)}
                    >
                        <MenuItem value="">{t("None")}</MenuItem>
                        {customers.map((c) => (
                            <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <TextField
                    margin="dense"
                    label={t("Mobile")}
                    fullWidth
                    value={contact.mobile}
                    onChange={(e) => handleChange("mobile", e.target.value)}
                />
                <TextField
                    margin="dense"
                    label={t("Phone")}
                    fullWidth
                    value={contact.phone}
                    onChange={(e) => handleChange("phone", e.target.value)}
                />
                <TextField
                    margin="dense"
                    label={t("Title")}
                    fullWidth
                    value={contact.title}
                    onChange={(e) => handleChange("title", e.target.value)}
                />
                <TextField
                    margin="dense"
                    label={t("Email Key")}
                    fullWidth
                    value={contact.emailKey}
                    onChange={(e) => handleChange("emailKey", e.target.value)}
                />
                {allowSurvey && (
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={contact.sendSatisfactionSurvey}
                                onChange={(e) =>
                                    setContact({ ...contact, sendSatisfactionSurvey: e.target.checked })
                                }
                            />
                        }
                        label={t("Send Satisfaction Survey")}
                    />
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={loading}>{t("Cancel")}</Button>
                <Button onClick={handleSave} disabled={loading} color="primary">
                    {loading ? <CircularProgress size={24} /> : (isEditing ? t("Update") : t("Save"))}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AddContactDialog;
